"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";

import { useExportEstimate } from "@/hooks/use-search";
import { useGetOrgComplianceSettings } from "@/lib/api/generated";
import { customInstance } from "@/lib/api/client";

import {
  ARTIFACT_FORMATS,
  DEFAULT_CSV_COLUMNS,
  NAME_MODE_RANK,
  estimateStateOf,
  requestExportTheme,
  type ArtifactFormat,
  type CsvColumnKey,
  type EstimateState,
  type ExportFormat,
  type ExportOptions,
  type ExportSource,
  type ExportTheme,
  type NameMode,
} from "./types";
import { usePreferredExportTheme } from "./use-preferred-export-theme";

export type ExportWizardStep = 1 | 2 | 3;

interface ExportWizardContextValue {
  step: ExportWizardStep;
  setStep: (step: ExportWizardStep) => void;
  formats: ArtifactFormat[];
  toggleFormat: (format: ArtifactFormat) => void;
  csvSelected: boolean;
  pdfSelected: boolean;
  includeContacts: boolean;
  handleContactsToggle: (checked: boolean) => void;
  verifiedOnly: boolean;
  setVerifiedOnly: (checked: boolean) => void;
  personalOnly: boolean;
  setPersonalOnly: (checked: boolean) => void;
  personalPreferred: boolean;
  setPersonalPreferred: (checked: boolean) => void;
  hideExporter: boolean;
  setHideExporter: (checked: boolean) => void;
  nameMode: NameMode;
  nameModeFloor: NameMode;
  setNameMode: (mode: NameMode) => void;
  includeHighlights: boolean;
  highlightsAllowedByOrg: boolean;
  setIncludeHighlights: (checked: boolean) => void;
  csvColumns: CsvColumnKey[];
  toggleCsvColumn: (key: CsvColumnKey) => void;
  theme: ExportTheme;
  setTheme: (theme: ExportTheme) => void;
  resolvedCount: number;
  creditsPerProfilePerFormat: number;
  creditsPerReveal: number;
  contactsToReveal: number;
  contactsAlreadyRevealed: number;
  contactCredits: number;
  /** Profiles still chargeable for each artifact, after the discount. */
  chargeableByFormat: Record<ArtifactFormat, number>;
  /** Profiles this org already paid for, per artifact. */
  reusedByFormat: Record<ArtifactFormat, number>;
  totalCredits: number;
  /** Whether the cost comes from the backend estimate or the fallbacks. */
  estimateState: EstimateState;
  currentBalance: number;
  balanceAfter: number;
  hasEnoughCredits: boolean;
  handleConfirm: () => void;
}

const ExportWizardContext = createContext<ExportWizardContextValue | null>(
  null,
);

export function useExportWizard(): ExportWizardContextValue {
  const ctx = useContext(ExportWizardContext);
  if (!ctx) {
    throw new Error("useExportWizard must be used within ExportWizardProvider");
  }
  return ctx;
}

interface ExportWizardProviderProps {
  open: boolean;
  profileCount: number;
  source?: ExportSource | null;
  onClose: () => void;
  onExport: (
    format: ExportFormat,
    includeContacts: boolean,
    options: ExportOptions,
  ) => void;
  children: ReactNode;
}

export function ExportWizardProvider({
  open,
  profileCount,
  source,
  onClose,
  onExport,
  children,
}: ExportWizardProviderProps) {
  const [step, setStep] = useState<ExportWizardStep>(1);
  const [formats, setFormats] = useState<ArtifactFormat[]>(["pdf"]);
  const [includeContacts, setIncludeContacts] = useState(false);
  const [hideExporter, setHideExporter] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [personalOnly, setPersonalOnlyState] = useState(false);
  const [personalPreferred, setPersonalPreferredState] = useState(false);
  // null = follow the org privacy default; set once the user picks manually.
  const [nameModeOverride, setNameModeOverride] = useState<NameMode | null>(
    null,
  );
  const [highlightsOverride, setHighlightsOverride] = useState<boolean | null>(
    null,
  );
  const [csvColumns, setCsvColumns] =
    useState<CsvColumnKey[]>(DEFAULT_CSV_COLUMNS);
  // null = follow the saved document theme; set once the user picks manually.
  const [themeOverride, setThemeOverride] = useState<ExportTheme | null>(null);
  const preferredTheme = usePreferredExportTheme();
  const theme = themeOverride ?? preferredTheme;

  useEffect(() => {
    if (open) {
      setStep(1);
      setFormats(["pdf"]);
      setIncludeContacts(false);
      setHideExporter(false);
      setVerifiedOnly(false);
      setPersonalOnlyState(false);
      setPersonalPreferredState(false);
      setNameModeOverride(null);
      setHighlightsOverride(null);
      setCsvColumns(DEFAULT_CSV_COLUMNS);
      setThemeOverride(null);
    }
  }, [open]);

  // Share the global credits cache; only fetch while the wizard is open so
  // a closed mount isn't an always-on observer that refetches on invalidation.
  const { data: creditData } = useQuery({
    queryKey: ["billing", "credits"],
    queryFn: () => customInstance<{ balance: number }>("/api/billing/credits"),
    enabled: open,
    staleTime: 30_000,
  });
  const currentBalance = creditData?.balance ?? 0;

  // Org privacy policy — the floor for name display and AI highlights.
  const { data: orgPrivacy } = useGetOrgComplianceSettings();
  const nameModeFloor: NameMode =
    orgPrivacy?.candidate_name_display_mode === "anonymized"
      ? "anonymized"
      : orgPrivacy?.candidate_name_display_mode === "initials_only"
        ? "initials_only"
        : "none";
  const nameMode: NameMode =
    nameModeOverride !== null &&
    NAME_MODE_RANK[nameModeOverride] >= NAME_MODE_RANK[nameModeFloor]
      ? nameModeOverride
      : nameModeFloor;
  const highlightsAllowedByOrg = orgPrivacy?.show_profile_highlights ?? true;
  const includeHighlights = highlightsAllowedByOrg
    ? (highlightsOverride ?? true)
    : false;

  // Backend pre-calculation: authoritative profile count, how many contacts
  // the org already revealed (never re-charged), and per-unit credit costs.
  // Without it we fall back to the caller's count and worst-case contact
  // pricing, and the review step says the cost is an estimate.
  const estimateQuery = useExportEstimate(open && source ? source : null);
  const estimateState = estimateStateOf(estimateQuery);
  const estimate = estimateState === "ready" ? estimateQuery.data : undefined;
  const resolvedCount = estimate?.profile_count ?? profileCount;
  const format: ExportFormat =
    formats.length === 2 ? "csv+pdf" : (formats[0] ?? "pdf");
  const creditsPerProfilePerFormat =
    estimate?.credits_per_profile_per_format ?? 1;
  const creditsPerReveal = estimate?.credits_per_contact_reveal ?? 2;
  const contactsAlreadyRevealed = estimate?.contacts_already_revealed ?? 0;
  const contactsToReveal = estimate?.contacts_to_reveal ?? resolvedCount;

  // Profiles this org has already paid to export in each artifact format,
  // which it never pays for again. Keyed by format because the user flips
  // format here without refetching the estimate. Missing keys mean nothing
  // is covered, which is also the pre-estimate fallback — so the displayed
  // total can only go down as the estimate resolves.
  const reusedByFormat = useMemo(() => {
    const covered = estimate?.profiles_already_exported ?? {};
    return ARTIFACT_FORMATS.reduce(
      (acc, fmt) => {
        acc[fmt] = Math.min(covered[fmt] ?? 0, resolvedCount);
        return acc;
      },
      {} as Record<ArtifactFormat, number>,
    );
  }, [estimate?.profiles_already_exported, resolvedCount]);

  const chargeableByFormat = useMemo(
    () =>
      ARTIFACT_FORMATS.reduce(
        (acc, fmt) => {
          acc[fmt] = resolvedCount - reusedByFormat[fmt];
          return acc;
        },
        {} as Record<ArtifactFormat, number>,
      ),
    [resolvedCount, reusedByFormat],
  );

  const exportCredits =
    formats.reduce((total, fmt) => total + chargeableByFormat[fmt], 0) *
    creditsPerProfilePerFormat;
  const contactCredits = includeContacts
    ? contactsToReveal * creditsPerReveal
    : 0;
  const totalCredits = exportCredits + contactCredits;
  const balanceAfter = currentBalance - totalCredits;
  const hasEnoughCredits = balanceAfter >= 0;

  const toggleFormat = useCallback((fmt: ArtifactFormat) => {
    setFormats((prev) =>
      prev.includes(fmt) ? prev.filter((f) => f !== fmt) : [...prev, fmt],
    );
  }, []);

  const toggleCsvColumn = useCallback((key: CsvColumnKey) => {
    setCsvColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }, []);

  const handleContactsToggle = useCallback((checked: boolean) => {
    setIncludeContacts(checked);
    if (!checked) {
      setVerifiedOnly(false);
      setPersonalOnlyState(false);
      setPersonalPreferredState(false);
    }
  }, []);

  const setPersonalOnly = useCallback((checked: boolean) => {
    setPersonalOnlyState(checked);
    if (checked) {
      setPersonalPreferredState(false);
    }
  }, []);

  const setPersonalPreferred = useCallback((checked: boolean) => {
    setPersonalPreferredState(checked);
    if (checked) {
      setPersonalOnlyState(false);
    }
  }, []);

  const handleConfirm = useCallback(() => {
    // Only while open: the confirm closes the wizard, and a double-click's
    // second click lands on it mid exit animation.
    if (
      !open ||
      estimateState === "loading" ||
      !hasEnoughCredits ||
      resolvedCount === 0 ||
      formats.length === 0
    )
      return;
    onClose();
    onExport(format, includeContacts, {
      name_redaction: nameMode,
      hide_exporter: hideExporter,
      verified_contacts_only: verifiedOnly,
      personal_emails_only: personalOnly,
      personal_emails_preferred: personalPreferred,
      include_highlights: includeHighlights,
      csv_columns: formats.includes("csv") ? csvColumns : null,
      theme: requestExportTheme(formats, theme),
    });
  }, [
    open,
    estimateState,
    hasEnoughCredits,
    resolvedCount,
    formats,
    onClose,
    onExport,
    format,
    includeContacts,
    nameMode,
    hideExporter,
    verifiedOnly,
    personalOnly,
    personalPreferred,
    includeHighlights,
    csvColumns,
    theme,
  ]);

  const value = useMemo<ExportWizardContextValue>(
    () => ({
      step,
      setStep,
      formats,
      toggleFormat,
      csvSelected: formats.includes("csv"),
      pdfSelected: formats.includes("pdf"),
      includeContacts,
      handleContactsToggle,
      verifiedOnly,
      setVerifiedOnly,
      personalOnly,
      setPersonalOnly,
      personalPreferred,
      setPersonalPreferred,
      hideExporter,
      setHideExporter,
      nameMode,
      nameModeFloor,
      setNameMode: setNameModeOverride,
      includeHighlights,
      highlightsAllowedByOrg,
      setIncludeHighlights: setHighlightsOverride,
      csvColumns,
      toggleCsvColumn,
      theme,
      setTheme: setThemeOverride,
      resolvedCount,
      creditsPerProfilePerFormat,
      creditsPerReveal,
      contactsToReveal,
      contactsAlreadyRevealed,
      contactCredits,
      chargeableByFormat,
      reusedByFormat,
      totalCredits,
      estimateState,
      currentBalance,
      balanceAfter,
      hasEnoughCredits,
      handleConfirm,
    }),
    [
      step,
      formats,
      toggleFormat,
      includeContacts,
      handleContactsToggle,
      verifiedOnly,
      personalOnly,
      personalPreferred,
      hideExporter,
      nameMode,
      nameModeFloor,
      includeHighlights,
      highlightsAllowedByOrg,
      csvColumns,
      toggleCsvColumn,
      theme,
      resolvedCount,
      creditsPerProfilePerFormat,
      creditsPerReveal,
      contactsToReveal,
      contactsAlreadyRevealed,
      contactCredits,
      chargeableByFormat,
      reusedByFormat,
      totalCredits,
      estimateState,
      currentBalance,
      balanceAfter,
      hasEnoughCredits,
      handleConfirm,
    ],
  );

  return (
    <ExportWizardContext.Provider value={value}>
      {children}
    </ExportWizardContext.Provider>
  );
}
